import { http } from '@/lib/http';

/** Mirrors `repo.EmployeeOwed` in the vizitpro-api swagger spec. */
export interface PayrollOwedDto {
  employee_id: string;
  full_name: string;
  email: string;
  visits_owed: number;
  total_owed: number;
}

/** Mirrors `repo.OwedVisit` in the vizitpro-api swagger spec. */
export interface PayrollOwedVisitDto {
  job_visit_employee_id: string;
  job_visit_id: string;
  job_id: string;
  scheduled_date: string;
  completed_at: string;
  pay_amount: number;
}

/** Mirrors `repo.PayrollPaymentItem` in the vizitpro-api swagger spec. */
export interface PayrollPaymentItemDto {
  id: string;
  payroll_payment_id: string;
  job_visit_employee_id: string;
  job_visit_id: string;
  amount: number;
  created_at: string;
}

/** Mirrors `repo.PayrollPayment` (header only) in the vizitpro-api swagger spec. */
export interface PayrollPaymentDto {
  id: string;
  business_id: string;
  employee_id: string;
  paid_at: string;
  total_amount: number;
  note: string | null;
  paid_by_user_id: string;
  created_at: string;
  updated_at: string;
}

/** Mirrors `repo.PayrollPaymentWithItems` in the vizitpro-api swagger spec. */
export interface PayrollPaymentDetailDto extends PayrollPaymentDto {
  items: PayrollPaymentItemDto[];
}

/** `from`/`to` (YYYY-MM-DD) are both optional and filter on visit `completed_at`. */
export interface PayrollDateRangeFilters {
  from?: string;
  to?: string;
}

/**
 * Body for `POST /businesses/{business_id}/payroll/payments` — mirrors
 * `controller.createPayrollPaymentRequest`. `job_visit_employee_ids` must be
 * exactly the ids returned by `listPayrollOwedForEmployee` for that
 * employee — this is bookkeeping only, no funds move through VizitPro.
 */
export interface CreatePayrollPaymentRequest {
  employee_id: string;
  job_visit_employee_ids: string[];
  note?: string;
}

export function payrollOwedQueryKey(businessId: string, filters: PayrollDateRangeFilters) {
  return ['payroll-owed', businessId, filters] as const;
}

export function payrollOwedDetailQueryKey(
  businessId: string,
  employeeId: string,
  filters: PayrollDateRangeFilters,
) {
  return ['payroll-owed', businessId, employeeId, filters] as const;
}

export function payrollPaymentsQueryKey(businessId: string, employeeId?: string) {
  return ['payroll-payments', businessId, employeeId ?? 'all'] as const;
}

export function payrollPaymentQueryKey(businessId: string, paymentId: string) {
  return ['payroll-payments', businessId, paymentId] as const;
}

/**
 * Calls `GET /businesses/{business_id}/payroll/owed`. Every employee with an
 * outstanding balance; employees with nothing owed are absent. Owner-only.
 */
export async function listPayrollOwed(
  businessId: string,
  filters: PayrollDateRangeFilters = {},
): Promise<PayrollOwedDto[]> {
  const { data } = await http.get<PayrollOwedDto[]>(
    `/businesses/${businessId}/payroll/owed`,
    { params: filters },
  );
  return data;
}

/**
 * Calls `GET /businesses/{business_id}/payroll/owed/{employee_id}`. The
 * individual completed-but-unpaid visit assignments behind one employee's
 * balance — also the source of the ids `createPayrollPayment` expects.
 * Owner-only.
 */
export async function listPayrollOwedForEmployee(
  businessId: string,
  employeeId: string,
  filters: PayrollDateRangeFilters = {},
): Promise<PayrollOwedVisitDto[]> {
  const { data } = await http.get<PayrollOwedVisitDto[]>(
    `/businesses/${businessId}/payroll/owed/${employeeId}`,
    { params: filters },
  );
  return data;
}

/**
 * Calls `POST /businesses/{business_id}/payroll/payments`. Records that the
 * business paid an employee for one or more visits — bookkeeping only, no
 * funds move through VizitPro. Owner-only. 400 if a selected visit is no
 * longer outstanding; 409 if one was already paid elsewhere in the meantime.
 */
export async function createPayrollPayment(
  businessId: string,
  body: CreatePayrollPaymentRequest,
): Promise<PayrollPaymentDetailDto> {
  const { data } = await http.post<PayrollPaymentDetailDto>(
    `/businesses/${businessId}/payroll/payments`,
    body,
  );
  return data;
}

/**
 * Calls `GET /businesses/{business_id}/payroll/payments`, optionally
 * filtered by employee. Newest first, headers only (no line items).
 * Owner-only.
 */
export async function listPayrollPayments(
  businessId: string,
  employeeId?: string,
): Promise<PayrollPaymentDto[]> {
  const { data } = await http.get<PayrollPaymentDto[]>(
    `/businesses/${businessId}/payroll/payments`,
    { params: employeeId ? { employee_id: employeeId } : undefined },
  );
  return data;
}

/** Calls `GET /businesses/{business_id}/payroll/payments/{id}`. Owner-only. */
export async function getPayrollPayment(
  businessId: string,
  paymentId: string,
): Promise<PayrollPaymentDetailDto> {
  const { data } = await http.get<PayrollPaymentDetailDto>(
    `/businesses/${businessId}/payroll/payments/${paymentId}`,
  );
  return data;
}
